<template>
    <v-container class="py-8 home">
        <div class="home__header">
            <span class="page-title">Olá{{ firstName ? `, ${firstName}` : '' }}</span>
            <p class="home__subtitle">Resumo do painel em {{ today }}.</p>
        </div>

        <v-row>
            <v-col v-for="card in cards" :key="card.label" cols="12" sm="6" md="4">
                <router-link :to="card.to" class="home-kpi">
                    <span class="home-kpi__icon"><v-icon color="primary">{{ card.icon }}</v-icon></span>
                    <span class="home-kpi__body">
                        <span class="home-kpi__value">{{ card.value }}</span>
                        <span class="home-kpi__label">{{ card.label }}</span>
                    </span>
                    <v-icon class="home-kpi__arrow" size="20">mdi-chevron-right</v-icon>
                </router-link>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12" lg="5">
                <v-card outlined class="home-card fill-height">
                    <div class="home-card__title">Prazos de habilitação abertos</div>

                    <div v-if="!deadlineEvents.length" class="home-card__empty">
                        <v-icon color="mediumGray">mdi-calendar-check-outline</v-icon>
                        Nenhum caso com prazo de habilitação aberto.
                    </div>

                    <div v-for="(item, index) in deadlineEvents" :key="index" class="home-deadline">
                        <div class="home-deadline__top">
                            <strong>{{ item.name }}</strong>
                            <span :class="['home-deadline__days', { 'home-deadline__days--urgent': item.totalDiff <= 3 }]">
                                {{ daysLabel(item) }}
                            </span>
                        </div>
                        <v-progress-linear :value="item.totalDiffPercentage" height="6" rounded
                            background-color="lightGray" :color="deadlineColor(item.totalDiffPercentage)" />
                        <div class="home-deadline__dates">
                            {{ item.start_event_at | defaultDate('DD/MM/YYYY') }} a {{ item.end_event_at | defaultDate('DD/MM/YYYY') }}
                        </div>
                    </div>
                </v-card>
            </v-col>

            <v-col cols="12" lg="7">
                <v-card outlined class="home-card fill-height">
                    <div class="home-card__title">Habilitações e divergências nos últimos 7 dias</div>
                    <highcharts class="hc" :options="eventsChart"></highcharts>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card outlined class="home-card">
                    <div class="home-card__title">Credores cadastrados nos últimos 7 dias</div>
                    <highcharts class="hc" :options="usersChart"></highcharts>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import 'moment/locale/pt-br';

import axios from "axios"

const NAVY = '#012a4a';
const COPPER = '#c98450';

// Base comum dos gráficos de colunas
const columnChart = (categories, series) => ({
    chart: { type: 'column', height: 260, style: { fontFamily: 'inherit' } },
    title: null,
    credits: { enabled: false },
    legend: { enabled: series.length > 1, itemStyle: { fontWeight: '600', color: '#17242e' } },
    xAxis: { categories, lineColor: '#e3e6ea', labels: { style: { color: '#66737c' } } },
    yAxis: { title: null, allowDecimals: false, gridLineColor: '#eef0f3', labels: { style: { color: '#66737c' } } },
    tooltip: { shared: true },
    plotOptions: { column: { borderRadius: 4, borderWidth: 0, pointPadding: 0.08, groupPadding: 0.18 } },
    series,
});

export default {
    data: () => ({
        countUsers: 0,
        countEvents: 0,
        countClients: 0,
        deadlineEvents: [],
        eventsChart: columnChart([], []),
        usersChart: columnChart([], []),
    }),

    computed: {
        firstName() {
            const user = this.$root.user && (this.$root.user.data || this.$root.user);
            const name = String((user && user.name) || '').trim().split(/\s+/)[0] || '';
            return name ? name.charAt(0).toUpperCase() + name.slice(1) : '';
        },

        today() {
            return this.$moment().format('DD [de] MMMM [de] YYYY');
        },

        cards() {
            // Só mostra o atalho para quem tem acesso à tela de destino
            return [
                { label: 'Hab/Div pendentes de análise', value: this.countEvents, icon: 'mdi-sync-alert', to: '/divergencias', path: 'Event' },
                { label: 'Casos cadastrados', value: this.countClients, icon: 'mdi-briefcase-outline', to: '/casos', path: 'Client' },
                { label: 'Credores cadastrados', value: this.countUsers, icon: 'mdi-account-group-outline', to: '/credores', path: 'User' },
            ].filter((card) => this.$check(card.path, 'GET'));
        },
    },

    created() {
        this.loadLastUsers();
        this.loadLastEvents();
        this.loadCounts();
        this.loadDeadlineEvents();
    },

    methods: {
        notifyError(error) {
            this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
        },

        // Últimos 7 dias, do mais antigo ao de hoje
        lastDays() {
            const days = [];
            for (let index = 6; index >= 0; index--) days.push(this.$moment().subtract(index, 'days'));
            return days;
        },

        seriesByDay(rows, field) {
            const days = this.lastDays().map((day) => day.format('YYYY-MM-DD'));
            const values = days.map(() => 0);

            for (const row of rows || []) {
                const index = days.indexOf(this.$moment(row.date).format('YYYY-MM-DD'));
                if (index !== -1) values[index] = Number(row[field] || 0);
            }

            return values;
        },

        categories() {
            return this.lastDays().map((day) => day.format('DD/MM'));
        },

        async loadLastUsers() {
            try {
                const result = await axios.get('/api/admin/dashboard/last-users');
                const rows = result.data.content;

                this.usersChart = columnChart(this.categories(), [
                    { name: 'Credores', data: this.seriesByDay(rows, 'total'), color: NAVY },
                ]);
            } catch (error) {
                this.notifyError(error);
            }
        },

        async loadLastEvents() {
            try {
                const result = await axios.get('/api/admin/dashboard/last-events');
                const rows = result.data.content;

                this.eventsChart = columnChart(this.categories(), [
                    { name: 'Habilitações', data: this.seriesByDay(rows, 'total_type1'), color: NAVY },
                    { name: 'Divergências', data: this.seriesByDay(rows, 'total_type2'), color: COPPER },
                ]);
            } catch (error) {
                this.notifyError(error);
            }
        },

        async loadCounts() {
            try {
                const [users, events, clients] = await Promise.all([
                    axios.get('/api/admin/dashboard/count-users'),
                    axios.get('/api/admin/dashboard/count-events'),
                    axios.get('/api/admin/dashboard/count-clients'),
                ]);

                this.countUsers = users.data.content;
                this.countEvents = events.data.content;
                this.countClients = clients.data.content;
            } catch (error) {
                this.notifyError(error);
            }
        },

        async loadDeadlineEvents() {
            try {
                const result = await axios.get('/api/admin/dashboard/deadline-events');
                const data = Array.isArray(result.data.content) ? result.data.content : [];

                this.deadlineEvents = data.map((item) => {
                    const start = this.$moment(item.start_event_at);
                    const end = this.$moment(item.end_event_at);
                    const duration = Math.max(end.diff(start, 'days'), 1);
                    const totalDiff = Math.max(end.diff(this.$moment(), 'days'), 0);

                    return {
                        ...item,
                        totalDiff,
                        totalDiffPercentage: Math.min(100, (100 * (duration - totalDiff)) / duration),
                    };
                });
            } catch (error) {
                this.notifyError(error);
            }
        },

        daysLabel(item) {
            if (item.totalDiff <= 0) return 'Último dia';
            return `${item.totalDiff} ${item.totalDiff === 1 ? 'dia' : 'dias'}`;
        },

        deadlineColor(percentage) {
            if (percentage >= 80) return 'error';
            if (percentage >= 50) return 'warning';
            return 'primary';
        },
    }
};
</script>

<style lang="scss" scoped>
.home__header {
    margin-bottom: 20px;
}

.home__subtitle {
    margin: 4px 0 0;
    color: var(--v-muted-base);
}

.home-kpi {
    display: flex;
    align-items: center;
    gap: 16px;
    height: 100%;
    padding: 20px;
    border: 1px solid var(--v-lightGray-base);
    border-radius: 10px;
    background: #fff;
    text-decoration: none;
    transition: border-color 160ms ease, box-shadow 160ms ease;

    &:hover {
        border-color: var(--v-copper-base);
        box-shadow: 0 6px 20px rgba(1, 42, 74, 0.08);
    }
}

.home-kpi__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: rgba(1, 42, 74, 0.07);
}

.home-kpi__body {
    display: flex;
    flex: 1;
    flex-direction: column;
}

.home-kpi__value {
    font-size: 28px;
    font-weight: 700;
    line-height: 1.1;
    color: var(--v-primary-base);
}

.home-kpi__label {
    font-size: 14px;
    color: var(--v-muted-base);
}

.home-kpi__arrow {
    color: var(--v-mediumGray-base) !important;
}

.home-card {
    padding: 20px 24px;
    background: #fff;
}

.home-card__title {
    margin-bottom: 16px;
    font-size: 16px;
    font-weight: 700;
    color: var(--v-primary-base);
}

.home-card__empty {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 24px 0;
    color: var(--v-muted-base);
}

.home-deadline {
    padding: 12px 0;
    border-bottom: 1px solid var(--v-lightGray-base);

    &:last-child {
        border-bottom: 0;
    }
}

.home-deadline__top {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
    color: var(--v-ink-base);
}

.home-deadline__days {
    flex-shrink: 0;
    font-size: 13px;
    font-weight: 700;
    color: var(--v-primary-base);
}

.home-deadline__days--urgent {
    color: var(--v-error-base);
}

.home-deadline__dates {
    margin-top: 6px;
    font-size: 12px;
    color: var(--v-muted-base);
}
</style>
